import type { HttpErrorResponse } from '@angular/common/http';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, of, startWith, Subscription, switchMap } from 'rxjs';
import {
  IInsurance,
  InsuranceCompanySearchResponseConnection,
} from 'src/app/shared/interfaces/insurance/insurance.interface';
import { IPayer } from 'src/app/shared/interfaces/payer/payers.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { InsuranceGQLService } from 'src/app/shared/services/insurance/insurance.graphql.service';
import { InsuranceService } from 'src/app/shared/services/insurance/insurance.service';
import { PayerGQLService } from 'src/app/shared/services/payers/payers.graphql.service';

@Component({
  selector: 'app-edit-insurance-setup-modal',
  templateUrl: './edit-insurance-setup-modal.component.html',
  styleUrls: ['./edit-insurance-setup-modal.component.scss'],
})
export class EditInsuranceSetupModalComponent implements OnInit, OnDestroy {
  @Input() payer: IPayer;
  @Input() insurance: InsuranceCompanySearchResponseConnection;

  insuranceForm: UntypedFormGroup;
  isEditMode = false;
  isLoading = true;

  insuranceApi?: IInsurance;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private insuranceGQLService: InsuranceGQLService,
    private alertService: AlertService,
    private insuranceService: InsuranceService,
    private payerGQLService: PayerGQLService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.isEditMode = !!this.insurance as boolean;

    this.setInsuranceForm();

    if (this.isEditMode) {
      this.getInsrance();
    } else {
      this.isLoading = false;
    }
  }

  save() {
    this.isLoading = true;

    const formValue: IInsurance = {
      ...this.insuranceApi,
      ...this.insuranceForm.getRawValue(),
      nexiaPayerMasterId: 0,
    };

    const request$ = this.isEditMode
      ? this.insuranceService.editInsuranceCompany(formValue)
      : this.insuranceService.createInsuranceCompany(formValue);

    this.subscriptions.add(
      request$.subscribe({
        next: () => {
          const successMessage = this.isEditMode
            ? 'Insurance was updated successfully!'
            : 'Insurance was created successfully!';
          this.alertService.success(successMessage);
          this.isLoading = false;
          this.activeModal.close();
        },
        error: (error: unknown) => {
          const insuranceFormName = this.insuranceForm.get('name');
          if (error) {
            if (
              (error as HttpErrorResponse).error.includes(
                'InsuranceCompany.Name',
              )
            ) {
              insuranceFormName?.markAsTouched();
              insuranceFormName?.setErrors({ notUnique: true });
            } else {
              this.alertService.error(error as HttpErrorResponse | string);
            }
          }

          this.isLoading = false;
        },
      }),
    );
  }

  close() {
    this.activeModal.dismiss();
  }

  private setInsuranceForm() {
    const { payer } = this;

    this.insuranceForm = this.fb.group({
      id: [{ value: payer.id, disabled: true }, [Validators.required]],
      insuranceCompanyCode: [''],
      payerState: [payer.payorState || '', [Validators.required]],
      claimFilingIndicator: [payer.claimFilingIndicator || ''],
      name: [payer.name || '', [Validators.required]],
      contactName: [payer.contactName || ''],
      contactPhone: [payer.contactPhone || ''],
      contactFax: [payer.contactFax || ''],
      claimStatusId: [payer.claimStatusId || ''],
      nexiaPayerMasterId: [''],
      recordStatus: [payer.recordStatus || ''],
      notes: [payer.notes || ''],
      claimStatus: [payer.claimStatus || ''],
      legacy: [payer.legacy || ''],
      nexia: [payer.nexia || ''],
      doesEligibility: [payer.doesEligibility || ''],
      eligibilityEnrollment: [payer.eligibilityEnrollment || ''],
      attachments: [payer.attachments || ''],
      claimStatusEnrollment: [payer.claimStatusEnrollment || ''],
      publishEra: [
        { value: payer.publishEra ? payer.publishEra : 'N', disabled: true },
      ],
      publishInstitutional: [
        {
          value: payer.ubProcessorId ? payer.publishInstitutional : 'N',
          disabled: payer.ubProcessorId ? false : true,
        },
      ],
      publishProfessional: [
        {
          value: payer.processorId ? payer.publishProfessional : 'N',
          disabled: payer.processorId ? false : true,
        },
      ],
      contactEmail: [payer.contactEmail || ''],
      neicId: [
        { value: payer.neicId || '', disabled: true },
        [Validators.required],
      ],
      webUrl: [payer.webUrl || ''],
      claimStatusType: [payer.claimStatusType || ''],
      eligibilityType: [payer.eligibilityType || ''],
      attachmentType: [payer.attachmentType || ''],
      attachmentEnrollment: [payer.attachmentEnrollment || ''],
      realtimeId: [payer.realTimeId || ''],
      secondaryClaims: [
        { value: payer.secondaryClaims || 'N', disabled: true },
      ],
      batchClaimStatus: [
        { value: payer.batchClaimStatus || 'N', disabled: true },
      ],

      publishClaimStatus: [
        { value: payer.claimStatusId ? 'Y' : 'N', disabled: true },
      ],
      publishEligibility: [
        { value: payer.realTimeId ? 'Y' : 'N', disabled: true },
      ],
      publishAttachment: [
        { value: payer.attachmentsProcessorId ? 'Y' : 'N', disabled: true },
      ],
      transactionProfessionalClaims: [
        { value: payer.processorId ? 'Y' : 'N', disabled: true },
      ],
      transactionInstitutionalClaims: [
        { value: payer.ubProcessorId ? 'Y' : 'N', disabled: true },
      ],
      transactionERA: [{ value: payer.eraEnrollment || 'N', disabled: true }],
      transactionEligibility: [
        { value: payer.realTimeId ? 'Y' : 'N', disabled: true },
      ],
      transactionClaimStatus: [
        { value: payer.claimStatusId ? 'Y' : 'N', disabled: true },
      ],
      transactionAttachments: [
        { value: payer.attachmentsProcessorId ? 'Y' : 'N', disabled: true },
      ],
    });

    this.subscriptions.add(
      combineLatest([
        this.insuranceForm
          .get('claimStatusId')
          ?.valueChanges.pipe(startWith(payer.claimStatusId)) ??
          of(payer.claimStatusId),
        this.insuranceForm
          .get('realtimeId')
          ?.valueChanges.pipe(startWith(payer.realTimeId)) ??
          of(payer.realTimeId),
      ]).subscribe({
        next: ([claimValue, eligibilityValue]) => {
          this.insuranceForm.patchValue({
            transactionClaimStatus: claimValue ? 'Y' : 'N',
            publishClaimStatus: claimValue ? 'Y' : 'N',
            transactionEligibility: eligibilityValue ? 'Y' : 'N',
            publishEligibility: eligibilityValue ? 'Y' : 'N',
          });
        },
      }),
    );
  }

  private getInsrance() {
    this.subscriptions.add(
      this.insuranceGQLService
        .getInsurance(this.insurance.recId)
        .pipe(
          switchMap((insurance) => {
            return combineLatest([
              // @ts-expect-error - type is invalid (TODO: Fix this)
              this.payerGQLService.getPayer(insurance.id as number),
              of(insurance),
            ]);
          }),
        )
        .subscribe({
          next: ([payer, insurance]) => {
            console.log(payer);
            console.log(insurance);

            const [firstPayer] = payer.nodes;
            this.payer = firstPayer;
            this.insuranceApi = this.isEditMode
              ? // @ts-expect-error - type is invalid (TODO: Fix this)
                (insurance as IInsurance)
              : undefined;

            this.insuranceForm.patchValue({
              ...this.insuranceApi,
            });

            this.isLoading = false;
          },
          error: (error: unknown) => {
            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
            this.isLoading = false;
          },
        }),
    );
  }
}
